// PickupRaceForm.tsx
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import InputFormik from '../../common/components/form/InputFormik';
import ComboBoxFormik from '../../common/components/form/ComboBoxFormik';
import { maskCNPJ, maskCPF, maskPhone } from '../../common/helpers/mask.helpers';
import { Modal } from 'flowbite-react';
import ButtonWithLoader from '../../common/components/ui/loading/ButtonLoading';
import { postVehiclespickupRacelinkCollector } from '../../common/services/vehicles.api';
import './PickupRace.scss';
import logo from '/lv-logo.svg';
import { useParams } from 'react-router-dom';
import { HiUserCircle } from 'react-icons/hi2';
import { HiLocationMarker } from 'react-icons/hi';
import { COMPANIES } from '../../common/helpers/company.helpers';

enum PixKeyType {
  CPF = 'CPF',
  CNPJ = 'CNPJ',
  Telefone = 'Telefone',
  Email = 'Email',
  NúmeroDeTelefone = 'Número de Telefone',
  ChaveAleatoria = 'Chave Aleatória',
}

const isValidCPF = (raw: string) => {
  const cpf = raw.replace(/\D+/g, '');
  if (!cpf || cpf.length !== 11 || /^([0-9])\1{10}$/.test(cpf)) return false;
  const calc = (t: number) => {
    let sum = 0;
    for (let i = 0; i < t; i++) sum += parseInt(cpf.charAt(i)) * (t + 1 - i);
    return ((sum * 10) % 11) % 10;
  };
  return calc(9) === +cpf[9] && calc(10) === +cpf[10];
};

export type inputPickupRaceForm = {
  cpf: string;
  pixKeyType: string;
  pixKey: string;
};

const validationSchema = Yup.object().shape({
  cpf: Yup.string()
    .required('CPF é obrigatório')
    .test('valid-cpf', 'CPF inválido', (v) => (v ? isValidCPF(v) : false)),
  pixKeyType: Yup.string()
    .required('Tipo de chave Pix é obrigatório')
    .notOneOf(['-'], 'Selecione um tipo de chave'),
  pixKey: Yup.string().required('Chave Pix é obrigatória'),
});

const enumOptions = [
  { value: '-', label: 'Selecione um tipo' },
  ...Object.values(PixKeyType).map((v) => ({ value: v, label: v })),
];

function PickupRaceForm() {
  const [companiesDict] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(COMPANIES).map(([id, info]) => [id, info.name])
    )
  );

  const { companyId } = useParams();
  const [formMessage, setFormMessage] = useState<
    { type: 'success' | 'error'; text: string } | null
  >(null);

  const getMaskFunction = (type: string) => {
    switch (type) {
      case PixKeyType.CPF:
        return maskCPF;
      case PixKeyType.CNPJ:
        return maskCNPJ;
      case PixKeyType.NúmeroDeTelefone:
        return maskPhone;
      default:
        return undefined;
    }
  };

  const submitLinkCollector = async (data: inputPickupRaceForm) => {
    if (!companyId) return { ok: false };

    try {
      const { ok, message } = await postVehiclespickupRacelinkCollector(data, companyId);

      if (ok) {
        setFormMessage({ type: 'success', text: message || 'Recolhedor vinculado com sucesso!' });
        setTimeout(() => setFormMessage(null), 10_000);
        return { ok: true };
      }
      setFormMessage({ type: 'error', text: message || 'Falha ao vincular o recolhedor.' });
      setTimeout(() => setFormMessage(null), 5_000);
      return { ok: false };
    } catch {
      setFormMessage({ type: 'error', text: 'Erro inesperado. Tente novamente.' });
      setTimeout(() => setFormMessage(null), 5_000);
      return { ok: false };
    }
  };

  const renderContent = () => {
    if (!companiesDict[companyId!]) {
      return (
        <div className="container__pickupRace_error flex flex-col items-center gap-4">
          <h2 className="text-red-600 text-2xl font-bold">Parâmetro obrigatório ausente</h2>
          <p className="text-red-500">
            A URL deve conter <code>:companyId</code>. Exemplo:&nbsp;
            <code>/vehicles/pickup-race/repossession/link-collector/&lt;companyId&gt;</code>
          </p>
        </div>
      );
    }

    return (
      <Modal size="5xl" show className="relative dark:text-black">
        <Modal.Header
          className="
          flex flex-col justify-center
          sm:flex-row sm:justify-between
          items-center gap-2 px-4 py-3 bg-white border-b
          header__pickupRace   
        "
        >
          {/* título */}
          <div className="flex items-center gap-2 header__pickupRace_title">
            <HiUserCircle className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600" />
            <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 dark:text-gray-900">
              Informe seus dados
            </h2>
          </div>

          {/* badge */}
          <div
            className="
            flex items-center gap-1
            bg-indigo-100 text-indigo-800
            px-3 py-1 rounded-full text-sm font-medium
            w-full sm:w-auto justify-center
            header__pickupRace_badge        /* NOVA */
          "
          >
            <HiLocationMarker className="w-5 h-5" />
            <span>Você está em: {companiesDict[companyId!]}</span>
          </div>
        </Modal.Header>

        <Formik<inputPickupRaceForm>
          initialValues={{ cpf: '', pixKeyType: '-', pixKey: '' }}
          validationSchema={validationSchema}
          onSubmit={submitLinkCollector}
          validateOnChange
          validateOnBlur
        >
          {({ setFieldValue, values, isSubmitting, isValid, handleSubmit }) => {
            const handleKeyTypeChange = (val: unknown) => {
              if (typeof val === 'string') {
                setFieldValue('pixKeyType', val);
                setFieldValue(
                  'pixKey',
                  val === PixKeyType.CPF ? values.cpf.replace(/\D+/g, '') : ''
                );
              }
            };

            return (
              <div className="w-full max-w-2xl mx-auto">
                <Form>
                  <Modal.Body className="form__body">
                    <InputFormik
                      label="CPF:"
                      name="cpf"
                      placeholder="000.000.000-00"
                      required
                      mask={maskCPF}
                    />

                    <ComboBoxFormik
                      name="pixKeyType"
                      label="Tipo de chave Pix:"
                      options={enumOptions}
                      onChange={handleKeyTypeChange}
                      required
                    />

                    <InputFormik
                      label="Chave Pix:"
                      name="pixKey"
                      placeholder="Digite a chave"
                      mask={getMaskFunction(values.pixKeyType)}
                      required
                      readonly={values.pixKeyType === PixKeyType.CPF}
                    />

                    <ButtonWithLoader
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={!isValid || isSubmitting}
                      onClick={handleSubmit}
                    >
                      Enviar
                    </ButtonWithLoader>

                    {formMessage && (
                      <div
                        className={`form-message ${formMessage.type === 'success' ? 'success' : 'error'
                          }`}
                      >
                        {formMessage.text}
                      </div>
                    )}
                  </Modal.Body>
                </Form>
              </div>
            );
          }}
        </Formik>
      </Modal>
    );
  };

  return (
    <div className="container__pickupRace_container bg-gray-50 min-h-screen">
      <div className="logo__pickup__race_form">
        <img src={logo} alt="Logo" className="h-20" />
      </div>

      <div className="container__pickupRace_form px-4">
        {renderContent()}
      </div>
    </div>
  );
}

export default PickupRaceForm;
